﻿<script setup lang="ts">
/* eslint-disable no-console */
    import { IntervalState } from '@/shared/intervalState.ts';
    import { onBeforeUnmount, ref } from 'vue';
    import { getRefresh } from '@/apps/reloadPrompt/reloadPrompt.ts';

    const UPDATE_CHECK_INTERVAL = 60000; // Check for updates every 1 minute

    const storedIntervals = IntervalState.inject();

    const needRefresh = ref<boolean>(false);
    const instanceId = ref<string | null>(null);

    onBeforeUnmount(() => {
        storedIntervals?.reloadPrompt.clearInterval();
    });

    storedIntervals?.reloadPrompt.setInterval(async () => {
        if (!navigator.onLine) {
            return;
        }
        try {
            const result = await getRefresh(instanceId.value);
            if (result.needsRefresh) {
                needRefresh.value = true;
            } else if (instanceId.value === null && result.instanceId !== null) {
                instanceId.value = result.instanceId;
            }
        } catch (e) {
            console.debug('[ReloadPrompt] - Refresh check failed', e);
        }
    }, UPDATE_CHECK_INTERVAL);

    async function removeServiceWorker() {
        console.debug(`[ReloadPrompt] - Attempting to remove ServiceWorker`);
        if (window.navigator && navigator.serviceWorker) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                console.debug(`[ReloadPrompt] - Removing serviceworker for scope ${registration.scope}`);
                const removed = await registration.unregister();
                console.debug(`[ReloadPrompt] - Removed worker: ${removed}`);
            }
        }
    }

    async function removeServiceWorkerCache() {
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(
                keys.filter(key => key.startsWith('workbox-') || key.includes('vite-pwa'))
                    .map(async key => {
                        console.debug(`[ReloadPrompt] - Trying to remove cache for key: ${key}`);
                        const deleted = await caches.delete(key);
                        console.debug(`[ReloadPrompt] - Removed cache for key ${key} : ${deleted}`);
                    })
            );
        }
    }

    async function reload() {
        try {
            await removeServiceWorkerCache();
        } catch (e) {
            console.debug('[ReloadPrompt] - Failed to remove caches', e);
        }
        try {
            await removeServiceWorker();
        } finally {
            window.location.reload();
        }
    }

</script>

<template>
    <div
        v-if="needRefresh">
        <b-notification
            class="reload-notification"
            type="is-warning is-light"
            aria-close-label="Close notification"
            has-icon
            role="alert">
            <span>A new version of CommonLibrary is available!
                Please save your work in all the open tabs and click update.</span>
            <b-button
                class="reload-button"
                type="is-info"
                label="Update"
                @click="reload" />
        </b-notification>
    </div>
</template>

<style>
.reload-notification {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80%;
    font-size: 1.3rem;

}

.reload-button {
    margin-left: 10px;

}
</style>
